import { Component, OnDestroy, OnInit } from '@angular/core';
import { WhiteLabel } from '@api/model/white-label';
import { ActivatedRoute, Router } from '@angular/router';
import { UseTerms } from '@api/model/use-terms';
import { UseTermsService } from '@api/service/use-terms.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { conditionalValidator } from '@api/util/validators';
import { SessionService } from '../../../service/session.service';
import { User } from '@api/model/user';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { takeUntil, timeout } from 'rxjs/operators';
import { WhiteLabelService } from '@api/service/white-label.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-use-terms-page',
  templateUrl: './use-terms-page.component.html',
  styleUrls: ['./use-terms-page.component.scss']
})
export class UseTermsPageComponent implements OnInit, OnDestroy {
  isEcondos = true;
  whiteLabel: WhiteLabel | null = null;
  useTerms: UseTerms | null = null;
  form: UntypedFormGroup;
  user!: User;

  hugerteBaseConfig = {
    menubar: false,
    branding: false,
    statusbar: false,
    language: 'pt_BR',
    height: '560',
    plugins: 'fullscreen template table hr',
    toolbar:
      'undo redo | fontselect fontsizeselect formatselect | bold italic underline | alignleft aligncenter alignright alignjustify | forecolor backcolor removeformat | fullscreen '
  };
  STATUS = {
    LOADING: 0,
    SUCCESS: 1,
    ERROR: 2
  };
  status: number | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private useTermsService: UseTermsService,
    private whiteLabelService: WhiteLabelService,
    private sessionService: SessionService,
    private router: Router,
    private notificationService: NzNotificationService
  ) {
    this.form = this.fb.group({
      updateType: ['patch', conditionalValidator(() => !!this.useTerms, Validators.required)],
      useTermsText: ['', Validators.required],
      privacyPolicyText: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    // Mudança do Constructor para o OnInit para evitar problemas de reutilização do componente
    // Além de evitar erros de inicialização das variáveis
    this.user = this.sessionService.user;

    this.route.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
      const whiteLabel = data['whiteLabel'] as WhiteLabel | undefined;
      if (whiteLabel) {
        this.isEcondos = false;
        this.whiteLabel = whiteLabel;
        this.useTerms = data['useTerms'] as UseTerms;
      } else {
        this.isEcondos = true;
        this.whiteLabel = null;
        this.useTerms = data['econdos'] as UseTerms;
      }

      if (this.useTerms) {
        this.setFormValues(this.useTerms);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setFormValues(useTerms: UseTerms) {
    this.form.get('useTermsText')?.setValue(useTerms.useTermsText || '');
    this.form.get('privacyPolicyText')?.setValue(useTerms.privacyPolicyText || '');
  }

  save() {
    if (this.form.valid) {
      const action = this.buildUseTerms();
      this.status = this.STATUS.LOADING;
      action.pipe(timeout(10000)).subscribe({
        next: () => {
          this.status = this.STATUS.SUCCESS;
          this.notificationService.create('success', 'Termos de Uso e Privacidade', 'Item salvo com sucesso');
          this.router.navigate(['home', this.whiteLabel ? 'whitelabels' : 'dashboard']);
        },
        error: () => {
          this.notificationService.create('error', 'Error', 'Não foi possível realizar a operação. Tente novamente');
          this.status = this.STATUS.ERROR;
        }
      });
    } else {
      Object.values(this.form.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  toggleUseTermsStatus() {
    if (this.useTerms) {
      const useTerms = this.useTerms;
      const value = !(useTerms.enabled as boolean);
      const id: string = useTerms._id as string;
      this.status = this.STATUS.LOADING;
      this.useTermsService.toggleStatus(id).subscribe({
        next: () => {
          this.notificationService.create(
            'success',
            'Status alterado com sucesso',
            'Termo de uso e privacidade ' + (value ? 'habilitado' : 'desabilitado') + ' com sucesso'
          );
          useTerms.enabled = value;
          this.status = this.STATUS.SUCCESS;
        },
        error: () => {
          this.notificationService.create('error', 'Erro', 'Não foi possível realizar a operação. Tente novamente');
          this.status = this.STATUS.ERROR;
        }
      });
    } else {
      this.notificationService.create('error', 'Erro', 'É necessário que exista um termo de uso');
    }
  }

  private buildUseTerms() {
    const data = {
      useTermsText: this.form.get('useTermsText')?.value,
      privacyPolicyText: this.form.get('privacyPolicyText')?.value
    };

    if (this.useTerms) {
      const updateType = this.form.get('updateType')?.value || 'patch';
      const updatedBy = this.user;
      const id: string = this.useTerms._id as string;

      return this.useTermsService.update(id, {
        updateType,
        updatedUseTerms: { ...data, updatedBy }
      });
    } else {
      const createdBy = this.user;
      const whiteLabel: string | null = this.whiteLabel ? (this.whiteLabel?._id as string) : null;
      const isEcondos = !this.whiteLabel;

      return this.useTermsService.create({
        ...data,
        createdBy,
        ...(whiteLabel && { whiteLabel }),
        ...(isEcondos && { isEcondos })
      });
    }
  }
}
